import {
  validateNotifyMeNameField,
  validateNotifyMeEmailField,
  clearNotifyMeFieldError,
  splitName,
  submitNotifyMeNotification
} from './notify-me.js';
import { findQuickViewVariant, readQuickViewVariantPayload } from './global-quick-view-logic.js';

function initQuickView(card) {
  if (card.dataset.stlQvBound) return;
  card.dataset.stlQvBound = 'true';

  const targetId = card.dataset.stlQvTarget;
  const dialog = targetId ? document.getElementById(targetId) : null;
  if (!(dialog instanceof HTMLDialogElement)) return;

  const dialogHandle = initDialog(dialog);

  const open = () => {
    if (!dialog.open) {
      // Capture the "fresh" snapshot right before the FIRST real open —
      // not at page load. At page load the multi-option picker's async
      // fetch hasn't resolved yet, so a snapshot taken then is just the
      // loading skeleton; every reopen was restoring to that stale
      // skeleton instead of the real initial selection.
      dialogHandle.ensureSnapshot();
      // accordion-custom's own `#handleMediaQueryChange` listener stays
      // bound for the accordion's whole lifetime — including while this
      // dialog sits closed — and force-closes any panel that doesn't carry
      // its `open-by-default-on-*` attributes whenever the mobile/desktop
      // breakpoint is crossed (window resize, rotation, DevTools
      // responsive mode). Description doesn't set those attributes, so if
      // that breakpoint was crossed while the dialog was closed, it could
      // silently end up closed again before this open — resetting it here,
      // not just on close, catches that case too.
      resetAccordionState(dialog);
      dialog.showModal();
      document.body.style.overflow = 'hidden';
      // A scrollable panel that goes through display:none (a closed
      // <dialog>) and back to visible keeps its old scroll offset once the
      // browser restores layout — a single requestAnimationFrame still
      // fires before that restore happens, so the reset gets clobbered.
      // Two nested rAFs (roughly one full extra frame) reliably land after
      // it.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => resetScrollPositions(dialog));
      });
    }
  };

  card.addEventListener('click', open);
  card.addEventListener('keydown', (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      open();
    }
  });

  initScrollLock(dialog);
}

function initScrollLock(dialog) {
  if (dialog.dataset.stlScrollLockBound) return;
  dialog.dataset.stlScrollLockBound = 'true';

  dialog.addEventListener('close', () => {
    document.body.style.overflow = '';
    resetScrollPositions(dialog);
  });
}

// .stl-qv-grid scrolls on mobile (single-column layout); .stl-qv-details
// scrolls independently on desktop (fixed-height popup, image column
// doesn't scroll) — reset both so a reopened dialog always starts at top
// regardless of viewport width.
function resetScrollPositions(dialog) {
  const grid = dialog.querySelector('.stl-qv-grid');
  if (grid instanceof HTMLElement) grid.scrollTop = 0;
  const details = dialog.querySelector('.stl-qv-details');
  if (details instanceof HTMLElement) details.scrollTop = 0;

  resetSlideshowsToFirstSlide(dialog);
  constrainGalleryImageSize(dialog);

  const activeGroup = dialog.querySelector('[data-stl-qv-gallery-group]:not([hidden])');
  if (activeGroup instanceof HTMLElement) markSlideImagesLoading(activeGroup);
}

// Desktop gallery image is a square box that normally derives its size
// from the column's WIDTH (aspect-ratio: 1). When the browser window gets
// short, the popup's own height shrinks with it, but nothing in the
// slideshow-component/slideshow-container/slideshow-slides/slideshow-slide/
// product-media-container chain reliably passes that shrink down through
// CSS alone — every percentage-height and flex-stretch approach tried here
// got silently defeated somewhere in that chain (verified in devtools: the
// image kept its full width-derived size and just overflowed, clipped by
// .stl-qv-gallery's overflow:hidden, cropping the thumbnail row beneath
// it). Sizing the image box directly in JS, from real measured space,
// sidesteps all of that unreliable cascading entirely.
function constrainGalleryImageSize(dialog) {
  const isMobile = window.matchMedia('(max-width: 749px)').matches;
  const group = dialog.querySelector('.stl-qv-gallery-group:not([hidden])');
  if (!(group instanceof HTMLElement)) return;
  const mediaBoxes = group.querySelectorAll('.product-media-container');

  if (isMobile) {
    // Mobile doesn't fix the popup's height (it scrolls the whole grid
    // instead), so the image never needs to be capped there — clear any
    // leftover inline sizing from a previous desktop-width measurement.
    mediaBoxes.forEach((el) => {
      el.style.removeProperty('width');
      el.style.removeProperty('height');
    });
    return;
  }

  const gallery = dialog.querySelector('.stl-qv-gallery');
  if (!(gallery instanceof HTMLElement)) return;
  const thumbs = group.querySelector('.stl-qv-thumbs');
  const thumbsHeight = thumbs instanceof HTMLElement ? thumbs.getBoundingClientRect().height : 0;

  // group.clientWidth, not gallery.clientWidth — .stl-qv-gallery has its
  // own 15px padding on every side, and the image sits inside .stl-qv-
  // gallery-group (already inset within that padding); using the outer
  // gallery's padding-box width let the image overflow the padding by
  // 15px on each side.
  const availableWidth = group.clientWidth;

  // .stl-qv-grid, not gallery.clientHeight — the gallery column no longer
  // flex-grows to fill the row (that was leaving a blank gap below the
  // thumbnails whenever the image was width-constrained rather than
  // height-constrained: the flex chain still stretched to fill the full
  // row even though the image itself had shrunk smaller than that). But
  // gallery.clientHeight now just reflects its own CONTENT height, which
  // includes the image box THIS function is about to resize — reading it
  // here would measure against whatever size the image happened to be
  // set to on the previous run, not the real available space. .stl-qv-grid
  // is pinned to the dialog's real height via grid-template-rows, so it's
  // a stable reference independent of the image's current size.
  const grid = dialog.querySelector('.stl-qv-grid');
  const galleryStyles = getComputedStyle(gallery);
  const galleryPaddingV = parseFloat(galleryStyles.paddingTop || '0') + parseFloat(galleryStyles.paddingBottom || '0');
  const rowHeight = grid instanceof HTMLElement ? grid.clientHeight : gallery.clientHeight;
  const availableHeight = rowHeight - galleryPaddingV - thumbsHeight;

  // Square image: fit within whichever dimension is tighter.
  const size = Math.max(0, Math.floor(Math.min(availableWidth, availableHeight)));

  mediaBoxes.forEach((el) => {
    el.style.setProperty('width', `${size}px`, 'important');
    el.style.setProperty('height', `${size}px`, 'important');
  });
}

// On iOS Safari, the slideshow's own scroll-snap-based "which slide is
// active" detection can misfire right after a hidden <dialog> becomes
// visible via showModal() — layout/scroll metrics aren't fully settled yet,
// so it sometimes reports a slide other than the first as current (a
// "random" image on open), even though the gallery scroller itself is at
// scrollLeft 0. Force every visible gallery's slideshow back to slide 0
// explicitly instead of trusting that detection on open.
function resetSlideshowsToFirstSlide(dialog) {
  dialog.querySelectorAll('.stl-qv-gallery-group:not([hidden]) slideshow-component').forEach((slideshow) => {
    if (typeof slideshow.select === 'function') {
      slideshow.select(0, undefined, { animate: false });
    }
  });
}

function initDialog(dialog) {
  if (dialog.dataset.stlQvBound) {
    // Already bound (defensive — in practice initQuickView's own guard
    // means this only ever runs once per dialog) — hand back a no-op
    // handle so callers can still call ensureSnapshot() safely.
    return { ensureSnapshot: () => {} };
  }
  dialog.dataset.stlQvBound = 'true';

  dialog.querySelector('[data-stl-qv-close]')?.addEventListener('click', () => dialog.close());

  dialog.addEventListener('click', (event) => {
    if (event.target === dialog) dialog.close();
  });

  // Captured lazily (see ensureSnapshot) instead of right here — at this
  // point (page load) the multi-option picker's async data fetch hasn't
  // resolved yet, so an eager capture would freeze-in the loading
  // skeleton as the "initial" state and every reopen would restore to
  // that stale skeleton instead of the real default selection.
  let snapshot = null;
  const ensureSnapshot = () => {
    if (!snapshot) snapshot = captureInitialState(dialog);
  };

  // Read variant JSON on each change. The global quick view reuses one
  // dialog element and swaps its children per product, so a list captured
  // at bind time would describe the first product opened.
  dialog.addEventListener('quick-view-multi-option-change', (event) => {
    const matchingId = event.detail?.matchingVariant?.id;
    if (!matchingId) return;

    const { variants, variantNames } = readQuickViewVariantPayload(dialog);
    const matched = findQuickViewVariant(variants, matchingId);
    if (!matched) return;

    const titleEl = dialog.querySelector('[data-stl-qv-title]');
    if (titleEl) {
      titleEl.textContent = variantNames[String(matchingId)] || titleEl.textContent.trim();
    }

    const skuEl = dialog.querySelector('[data-stl-qv-sku]');
    if (skuEl) skuEl.textContent = matched.sku || '';

    const variantInput = dialog.querySelector('[data-stl-qv-variant-input]');
    renderVariantState(dialog, matched, variantInput);
    showMatchingGallery(dialog, matchingId);
    constrainGalleryImageSize(dialog);

    if (window.matchMedia('(max-width: 749px)').matches) {
      const grid = dialog.querySelector('.stl-qv-grid');
      if (grid instanceof HTMLElement) grid.scrollTop = 0;
    }
  });

  dialog.addEventListener('close', () => {
    restoreInitialState(dialog, snapshot);
    resetAccordionState(dialog);
  });

  initNotifyMe(dialog);
  initSizeChart(dialog);
  initHintToggle(dialog);
  hideEmptyRteParagraphs(dialog);

  return { ensureSnapshot };
}

// Rich-text metafields (description/FAQ) sometimes contain blank `<p>&nbsp;</p>`
// lines from the editor — these aren't caught by the theme's global `p:empty`
// CSS rule (they contain a non-breaking-space text node, so they aren't truly
// empty), which shows up as extra vertical gaps when the accordion is
// expanded. Same fix PDP's product-accordion block uses for its own
// description rich text.
function hideEmptyRteParagraphs(dialog) {
  dialog.querySelectorAll('.stl-qv-accordion__section.rte p').forEach((p) => {
    if (p.innerHTML.replace(/&nbsp;|\s|<[^>]*>/g, '') === '') {
      p.style.display = 'none';
    }
  });
}

function captureInitialState(dialog) {
  const picker = dialog.querySelector('quick-view-multi-option');

  return {
    pickerHtml: picker?.outerHTML || null,
    titleText: dialog.querySelector('[data-stl-qv-title]')?.textContent || '',
    skuText: dialog.querySelector('[data-stl-qv-sku]')?.textContent || '',
    priceText: dialog.querySelector('[data-stl-qv-price]')?.textContent || '',
    comparePriceText: dialog.querySelector('[data-stl-qv-compare-price]')?.textContent || '',
    comparePriceHidden: dialog.querySelector('[data-stl-qv-compare-price]')?.hidden ?? true,
    discountText: dialog.querySelector('[data-stl-qv-discount]')?.textContent || '',
    discountHidden: dialog.querySelector('[data-stl-qv-discount]')?.hidden ?? true,
    addToCartDisabled: dialog.querySelector('[data-stl-qv-add-to-cart]')?.disabled ?? false,
    variantInputValue: dialog.querySelector('[data-stl-qv-variant-input]')?.value || '',
    notifyMeHidden: dialog.querySelector('[data-stl-qv-notify-me]')?.hidden ?? true,
    galleryGroupsHidden: Array.from(dialog.querySelectorAll('[data-stl-qv-gallery-group]')).map((group) => group.hidden),
    // Full slideshow markup per group, so a reopen can drop in a genuinely
    // fresh instance rather than fighting the live component's internal
    // current-slide/scroll-position state (which select(0) wasn't
    // reliably resetting — the highlighted thumbnail would reset but the
    // actually-displayed slide wouldn't).
    slideshowsHtml: Array.from(dialog.querySelectorAll('[data-stl-qv-gallery-group]')).map(
      (group) => group.querySelector('slideshow-component')?.outerHTML || null
    )
  };
}

function restoreInitialState(dialog, snapshot) {
  if (!snapshot) return;

  const picker = dialog.querySelector('quick-view-multi-option');
  if (picker && snapshot.pickerHtml) {
    const template = document.createElement('template');
    template.innerHTML = snapshot.pickerHtml;
    const fresh = template.content.firstElementChild;
    if (fresh) picker.replaceWith(fresh);
  }

  const titleEl = dialog.querySelector('[data-stl-qv-title]');
  if (titleEl) titleEl.textContent = snapshot.titleText;

  const skuEl = dialog.querySelector('[data-stl-qv-sku]');
  if (skuEl) skuEl.textContent = snapshot.skuText;

  const priceEl = dialog.querySelector('[data-stl-qv-price]');
  if (priceEl) priceEl.textContent = snapshot.priceText;

  const comparePriceEl = dialog.querySelector('[data-stl-qv-compare-price]');
  if (comparePriceEl instanceof HTMLElement) {
    comparePriceEl.textContent = snapshot.comparePriceText;
    comparePriceEl.hidden = snapshot.comparePriceHidden;
  }

  const discountEl = dialog.querySelector('[data-stl-qv-discount]');
  if (discountEl instanceof HTMLElement) {
    discountEl.textContent = snapshot.discountText;
    discountEl.hidden = snapshot.discountHidden;
  }

  const addToCartBtn = dialog.querySelector('[data-stl-qv-add-to-cart]');
  if (addToCartBtn instanceof HTMLButtonElement) {
    addToCartBtn.disabled = snapshot.addToCartDisabled;
  }

  const variantInput = dialog.querySelector('[data-stl-qv-variant-input]');
  if (variantInput instanceof HTMLInputElement) {
    variantInput.value = snapshot.variantInputValue;
  }

  const notifyMeWrapper = dialog.querySelector('[data-stl-qv-notify-me]');
  if (notifyMeWrapper instanceof HTMLElement) {
    notifyMeWrapper.hidden = snapshot.notifyMeHidden;
    const formEl = notifyMeWrapper.querySelector('quick-view-notify-me-form');
    if (formEl instanceof HTMLElement) formEl.dataset.subscribed = 'false';
  }

  const groups = dialog.querySelectorAll('[data-stl-qv-gallery-group]');
  groups.forEach((group, index) => {
    if (group instanceof HTMLElement) group.hidden = snapshot.galleryGroupsHidden[index] ?? group.hidden;
  });

  // Drop in a fresh slideshow instance per group instead of trying to move
  // the live one back to slide 0 — the component tracks its own current
  // slide/scroll position internally, and asking it to select(0) left the
  // thumbnail highlight reset but the actually-displayed slide unchanged.
  // A clone reinitializes cleanly, always showing its first slide.
  groups.forEach((group, index) => {
    if (!(group instanceof HTMLElement)) return;
    const html = snapshot.slideshowsHtml?.[index];
    const current = group.querySelector('slideshow-component');
    if (!html || !current) return;
    const template = document.createElement('template');
    template.innerHTML = html;
    const fresh = template.content.firstElementChild;
    if (fresh) current.replaceWith(fresh);
  });

  const mainEl = dialog.querySelector('[data-stl-qv-details-main]');
  const chartEl = dialog.querySelector('[data-stl-qv-size-chart]');
  if (mainEl instanceof HTMLElement) mainEl.hidden = false;
  if (chartEl instanceof HTMLElement) chartEl.hidden = true;
}

// Collapse the Product Highlights accordions (Description/Specification/
// FAQ/Drop A Hint) back to their default state — otherwise whichever ones
// the shopper had open would stay open on reopen. The Description row is
// open by default (marked with data-stl-qv-default-open in the markup),
// so it goes back to open instead of closed like the rest. Kept separate
// from restoreInitialState (and NOT gated behind having a snapshot) — this
// doesn't depend on any captured data, and gating it the same way meant
// the accordion silently stayed however the shopper last left it whenever
// close fired before ensureSnapshot() had captured anything yet.
function resetAccordionState(dialog) {
  dialog.querySelectorAll('.stl-qv-accordion__details[open]').forEach((details) => {
    details.open = false;
  });
  const defaultOpenDetails = dialog.querySelector('[data-stl-qv-default-open]');
  if (defaultOpenDetails instanceof HTMLDetailsElement) defaultOpenDetails.open = true;
  const hintToggle = dialog.querySelector('[data-stl-qv-hint-toggle]');
  const hintContent = dialog.querySelector('[data-stl-qv-hint-content]');
  if (hintToggle instanceof HTMLElement) hintToggle.setAttribute('aria-expanded', 'false');
  if (hintContent instanceof HTMLElement) hintContent.hidden = true;
}

function initNotifyMe(dialog) {
  const wrapper = dialog.querySelector('[data-stl-qv-notify-me]');
  const formEl = wrapper?.querySelector('quick-view-notify-me-form');
  if (!(wrapper instanceof HTMLElement) || !(formEl instanceof HTMLElement)) return;
  if (formEl.dataset.stlNotifyBound) return;
  formEl.dataset.stlNotifyBound = 'true';

  const nameInput = formEl.querySelector('[data-notify-me-name]');
  const emailInput = formEl.querySelector('[data-notify-me-email]');
  const skuInput = formEl.querySelector('[data-notify-me-sku]');
  const variantIdInput = formEl.querySelector('[data-notify-me-variant-id]');
  const submitBtn = formEl.querySelector('[data-notify-me-submit]');

  if (submitBtn instanceof HTMLElement) submitBtn.classList.remove('visually-hidden');
  if (nameInput instanceof HTMLInputElement) {
    nameInput.addEventListener('input', () => clearNotifyMeFieldError(nameInput));
  }
  if (emailInput instanceof HTMLInputElement) {
    emailInput.addEventListener('input', () => clearNotifyMeFieldError(emailInput));
  }

  submitBtn?.addEventListener('click', async () => {
    if (formEl.dataset.subscribed === 'true' || formEl.dataset.submitting === 'true') return;

    const isNameValid = !(nameInput instanceof HTMLInputElement) || validateNotifyMeNameField(nameInput, formEl);
    const isEmailValid = !(emailInput instanceof HTMLInputElement) || validateNotifyMeEmailField(emailInput, formEl);
    if (!isNameValid || !isEmailValid) return;

    const sku = skuInput instanceof HTMLInputElement ? skuInput.value : '';
    const variantId = variantIdInput instanceof HTMLInputElement ? variantIdInput.value : '';
    const email = emailInput instanceof HTMLInputElement ? emailInput.value.trim() : '';
    const name = nameInput instanceof HTMLInputElement ? nameInput.value.trim() : '';
    if (!sku || !email) return;

    formEl.dataset.submitting = 'true';
    if (submitBtn instanceof HTMLButtonElement) submitBtn.disabled = true;

    const { firstName, lastName } = splitName(name);

    try {
      const response = await submitNotifyMeNotification(
        {
          sku,
          email,
          customerFirstName: firstName,
          customerLastName: lastName,
          variantId,
          customerId: formEl.dataset.customerId || undefined
        },
        formEl.dataset.apiBaseUrl || '/apps/notify-me/v1'
      );

      const data = response.data;
      const success = data?.success ||
        data?.code === 'OK' ||
        data?.code === 'NOTIFICATION_EXISTS' ||
        response.status === 200 ||
        response.status === 201 ||
        response.status === 409;

      if (success) formEl.dataset.subscribed = 'true';
    } catch {
    } finally {
      formEl.dataset.submitting = 'false';
      if (submitBtn instanceof HTMLButtonElement) submitBtn.disabled = false;
    }
  });
}

function initSizeChart(dialog) {
  const mainEl = dialog.querySelector('[data-stl-qv-details-main]');
  const chartEl = dialog.querySelector('[data-stl-qv-size-chart]');
  if (!(mainEl instanceof HTMLElement) || !(chartEl instanceof HTMLElement)) return;
  if (dialog.dataset.stlSizeChartBound) return;
  dialog.dataset.stlSizeChartBound = 'true';

  dialog.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;

    if (event.target.closest('[data-size-chart-link]')) {
      event.preventDefault();
      mainEl.hidden = true;
      chartEl.hidden = false;
      return;
    }

    if (event.target.closest('[data-stl-qv-size-chart-back]')) {
      event.preventDefault();
      chartEl.hidden = true;
      mainEl.hidden = false;
    }
  });
}

function initHintToggle(dialog) {
  const toggle = dialog.querySelector('[data-stl-qv-hint-toggle]');
  const content = dialog.querySelector('[data-stl-qv-hint-content]');
  if (!(toggle instanceof HTMLElement) || !(content instanceof HTMLElement)) return;
  if (dialog.dataset.stlHintToggleBound) return;
  dialog.dataset.stlHintToggleBound = 'true';

  toggle.addEventListener('click', () => {
    const isOpen = !content.hidden;
    content.hidden = isOpen;
    toggle.setAttribute('aria-expanded', String(!isOpen));
  });
}

function showMatchingGallery(dialog, variantId) {
  const groups = dialog.querySelectorAll('[data-stl-qv-gallery-group]');
  groups.forEach((group) => {
    if (!(group instanceof HTMLElement)) return;
    const ids = (group.dataset.variantIds || '').split(' ').filter(Boolean);
    group.hidden = !ids.includes(String(variantId));
  });

  const activeGroup = dialog.querySelector('[data-stl-qv-gallery-group]:not([hidden])');
  if (activeGroup instanceof HTMLElement) markSlideImagesLoading(activeGroup);
}

// The image box itself keeps its size (aspect-ratio: 1, set on
// .product-media-container) whether or not the <img> inside has finished
// loading — so swapping groups doesn't collapse any space. What it did look
// like without this is a blank/white flash for however long the new image
// takes to load. Show the box's own gray background (a simple skeleton)
// under the image and fade the image in only once it's actually ready.
function markSlideImagesLoading(container) {
  container.querySelectorAll('.stl-qv-slide-image').forEach((img) => {
    if (!(img instanceof HTMLImageElement)) return;
    const box = img.closest('.product-media-container');
    if (!(box instanceof HTMLElement)) return;
    if (img.complete) {
      box.classList.remove('stl-qv-media--loading');
      return;
    }
    box.classList.add('stl-qv-media--loading');
    const onDone = () => {
      box.classList.remove('stl-qv-media--loading');
      img.removeEventListener('load', onDone);
      img.removeEventListener('error', onDone);
    };
    img.addEventListener('load', onDone);
    img.addEventListener('error', onDone);
  });
}

function renderVariantState(dialog, matched, variantInput) {
  if (variantInput instanceof HTMLInputElement) {
    variantInput.value = String(matched.id);
  }

  const priceEl = dialog.querySelector('[data-stl-qv-price]');
  if (priceEl) priceEl.textContent = formatMoney(matched.price);

  const comparePriceEl = dialog.querySelector('[data-stl-qv-compare-price]');
  const discountEl = dialog.querySelector('[data-stl-qv-discount]');
  const estimatedRetailPrice = Number(matched.estimated_retail_price) || 0;
  const compareValue = estimatedRetailPrice > matched.price ? estimatedRetailPrice : matched.compare_at_price;
  const showCompare = compareValue > matched.price;
  if (comparePriceEl instanceof HTMLElement) {
    comparePriceEl.hidden = !showCompare;
    if (showCompare) comparePriceEl.textContent = formatMoney(compareValue);
  }
  if (discountEl instanceof HTMLElement) {
    discountEl.hidden = !showCompare;
    if (showCompare) {
      const pct = Math.round(((compareValue - matched.price) / compareValue) * 100);
      discountEl.textContent = `${pct}% Off`;
    }
  }

  const addToCartBtn = dialog.querySelector('[data-stl-qv-add-to-cart]');
  if (addToCartBtn instanceof HTMLButtonElement) {
    addToCartBtn.disabled = !matched.available;
  }

  updateNotifyMe(dialog, matched);
}

function updateNotifyMe(dialog, matched) {
  const wrapper = dialog.querySelector('[data-stl-qv-notify-me]');
  if (!(wrapper instanceof HTMLElement)) return;

  wrapper.hidden = matched.available;
  if (matched.available) return;

  const formEl = wrapper.querySelector('quick-view-notify-me-form');
  if (!(formEl instanceof HTMLElement)) return;

  const skuInput = formEl.querySelector('[data-notify-me-sku]');
  const variantIdInput = formEl.querySelector('[data-notify-me-variant-id]');
  if (skuInput instanceof HTMLInputElement) skuInput.value = matched.sku || '';
  if (variantIdInput instanceof HTMLInputElement) variantIdInput.value = String(matched.id);

  formEl.dataset.subscribed = 'false';
  formEl.dataset.variantId = String(matched.id);
  formEl.dataset.variantSku = matched.sku || '';
}

function formatMoney(cents) {
  return `$${(cents / 100).toFixed(2)}`;
}

function init(root = document) {
  root.querySelectorAll('[data-stl-qv-trigger]').forEach((card) => {
    if (card instanceof HTMLElement) initQuickView(card);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => init());
} else {
  init();
}

// Re-measure the gallery image whenever the window is resized (e.g. the
// browser un/minimized, or rotated) while a quick view is open — otherwise
// the sizing set on open only reflects the viewport at that moment.
let stlQvResizeRaf = null;
window.addEventListener('resize', () => {
  if (stlQvResizeRaf) cancelAnimationFrame(stlQvResizeRaf);
  stlQvResizeRaf = requestAnimationFrame(() => {
    document.querySelectorAll('.stl-qv-dialog[open]').forEach((dialog) => {
      constrainGalleryImageSize(dialog);
    });
  });
});

// Used by the global quick view after it injects this same dialog markup.
// Binds notify-me / size chart once, then remeasures the gallery for the
// content that was just inserted.
export function refreshQuickViewLayout(dialog) {
  if (!(dialog instanceof HTMLDialogElement)) return;
  // First inject binds variant, size-chart, and hint listeners on the shell
  // dialog. Later injects reuse those listeners and only rebind notify-me,
  // which is attached to the form that was just replaced.
  initDialog(dialog);
  initNotifyMe(dialog);
  hideEmptyRteParagraphs(dialog);
  requestAnimationFrame(() => {
    requestAnimationFrame(() => resetScrollPositions(dialog));
  });
}

new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    for (const node of mutation.addedNodes) {
      if (node instanceof HTMLElement) init(node);
    }
  }
}).observe(document.body, { childList: true, subtree: true });
